/* eslint-disable */
import React, { useEffect, useMemo } from "react";
import {
  Button,
  Group,
  TextInput,
  Loader,
  Center,
  NumberInput,
  Select,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { z } from "zod";
import { useParams } from "react-router-dom";

import { useEmploymentHistory } from "api/users/getEmployementHistory";
import { useUpdateEmploymentHistory } from "api/users/hiringDetailUpdate";
import { useCreateEmploymentHistory } from "api/users/addEmployementHistory";

type FormValues = {
  startDate?: string | null;
  endDate?: string | null;
  position: string;
  salaryAmount?: number | null;
  salaryType: string;
  currency: string;
};

const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

const createHiringSchema = z
  .object({
    startDate: z
      .string()
      .optional()
      .nullable()
      .refine((v) => !v || dateRegex.test(v), {
        message: "Invalid start date",
      }),
    endDate: z
      .string()
      .optional()
      .nullable()
      .refine((v) => !v || dateRegex.test(v), { message: "Invalid end date" }),

    position: z.string().trim().min(1, { message: "Please enter position" }),

    salaryAmount: z
      .number()
      .optional()
      .nullable(),

    salaryType: z.string().trim().optional().nullable(),
    currency: z.string().trim().optional().nullable(),
  })
  .refine(
    (data) =>
      !!(data.startDate && String(data.startDate).trim() !== "") ||
      !!(data.endDate && String(data.endDate).trim() !== ""),
    {
      message: "Please provide start date or end date",
      path: ["startDate"],
    }
  )

  .refine(
    (data) => {
      if (
        data.startDate &&
        String(data.startDate).trim() !== "" &&
        data.endDate &&
        String(data.endDate).trim() !== ""
      ) {
        return String(data.startDate) <= String(data.endDate);
      }
      return true;
    },
    {
      message: "Start date must be before or equal to End date",
      path: ["endDate"],
    }
  );

export default function AddHiringDetailForm({
  onClose,
}: {
  onClose: () => void;
}) {
  const { userid } = useParams();
  const addHiringDetailMutation = useCreateEmploymentHistory();
  const { data: employmenthistory, isLoading: isLoadingHistory } =
    useEmploymentHistory(userid!);
  const hiringDetailUpdateMutation = useUpdateEmploymentHistory();

  const firstRecord = useMemo(() => {
    return employmenthistory?.data?.employmentHistory &&
      Array.isArray(employmenthistory.data.employmentHistory) &&
      employmenthistory.data.employmentHistory.length > 0
      ? employmenthistory.data.employmentHistory[0]
      : null;
  }, [employmenthistory]);

  const form = useForm<FormValues>({
    initialValues: {
      startDate: "",
      endDate: "",
      position: "",
      salaryAmount: undefined,
      salaryType: "annual",
      currency: "",
    },
    validate: zodResolver(createHiringSchema),
  });

  useEffect(() => {
    if (!firstRecord) return;

    form.setValues({
      startDate: firstRecord.startDate || "",
      endDate: firstRecord.endDate || "",
      position: firstRecord.position || "",
      salaryAmount: firstRecord.salaryAmount ?? undefined,
      salaryType: firstRecord.salaryType || "annual",
      currency: firstRecord.currency || "",
    });
  }, [firstRecord]);

  const handleSubmit = (values: FormValues) => {
    if (!userid) {
      showNotification({
        title: "Error",
        message: "Missing user ID — cannot save hiring details.",
        color: "red",
      });
      return;
    }

    const payload: any = {
      position: values.position.trim(),
    };

    if (values.startDate && String(values.startDate).trim() !== "") {
      payload.startDate = values.startDate;
    }
    if (values.endDate && String(values.endDate).trim() !== "") {
      payload.endDate = values.endDate;
    }
    if (values.salaryAmount != null) {
      payload.salaryAmount = Number(values.salaryAmount);
    }
    if (values.salaryType && String(values.salaryType).trim() !== "") {
      payload.salaryType = values.salaryType;
    }
    if (values.currency && String(values.currency).trim() !== "") {
      payload.currency = values.currency;
    }

    addHiringDetailMutation.mutate(
      { userId: userid, payload },
      {
        onSuccess: () => {
          showNotification({
            title: "Success",
            message: "Hiring details added successfully.",
            color: "green",
          });
          onClose();
        },
        onError: (err: any) => {
          const message =
            err?.response?.data?.message ??
            err?.message ??
            "Failed to add hiring details";
          showNotification({ title: "Error", message, color: "red" });
        },
      }
    );
  };

  if (isLoadingHistory) {
    return (
      <Center style={{ padding: 20 }}>
        <Loader />
      </Center>
    );
  }

  const isUpdateMode = Boolean(firstRecord && firstRecord._id);
  const submitLoading = isUpdateMode
    ? hiringDetailUpdateMutation.isLoading
    : addHiringDetailMutation.isLoading;

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <TextInput
        label="Start Date"
        placeholder="YYYY-MM-DD"
        type="date"
        mb={12}
        {...form.getInputProps("startDate")}
      />

      <TextInput
        label="End Date"
        placeholder="YYYY-MM-DD"
        type="date"
        mb={12}
        {...form.getInputProps("endDate")}
      />

      <TextInput
        label="Position"
        withAsterisk
        mb={12}
        {...form.getInputProps("position")}
      />

      <NumberInput
        label="Salary Amount"
        mb={12}
        precision={2}
        step={0.01}
        min={0}
        {...form.getInputProps("salaryAmount")}
      />

      <Select
        label="Salary Type"
        mb={12}
        data={[
          { value: "annual", label: "Annual" },
          { value: "daily", label: "Daily" },
          { value: "hourly", label: "Hourly" },
          { value: "biweekly", label: "Biweekly" },
        ]}
        {...form.getInputProps("salaryType")}
      />

      <Select
        label="Currency"
        placeholder="Select currency"
        searchable
        mb={12}
        data={[
          { value: "PKR", label: "PKR - Pakistani Rupee" },
          { value: "USD", label: "USD - US Dollar" },
          { value: "EUR", label: "EUR - Euro" },
          { value: "GBP", label: "GBP - British Pound" },
          { value: "AED", label: "AED - UAE Dirham" },
          { value: "SAR", label: "SAR - Saudi Riyal" },
          { value: "INR", label: "INR - Indian Rupee" },
          { value: "CAD", label: "CAD - Canadian Dollar" },
          { value: "AUD", label: "AUD - Australian Dollar" },
        ]}
        {...form.getInputProps("currency")}
      />

      <Group position="right" spacing={20} mt={20}>
        <Button color="cancel" onClick={onClose} type="button">
          Cancel
        </Button>
        <Button type="submit" variant="filled" loading={submitLoading}>
          ADD
        </Button>
      </Group>
    </form>
  );
}
